import React, { useMemo, useRef, useState } from "react";
import { InsightsTimeline, fetchInsightsByPatient } from "../Insights";
import { useToast } from "../../hooks/use-toast";
import labelMappings from "../Trace/labels.json";
import vizConfigJson from "./viz-config.json";

// Keep config loosely typed because some keys are optional by environment.
const vizConfig: any = vizConfigJson;

type TraceEvent = {
  patient_id: string;
  lambda_name: string;
  status: string; // "START" | "END" | others
  timestamp: number | string;
  uuid: string;
  meta?: any;
};

type Segment = {
  lambda_name: string;
  uuid: string;
  start: number; // epoch ms
  end: number; // epoch ms
  duration: number; // ms
  patient_id: string;
  // Optional diagnostics from insights
  summary?: string | string[];
  errors?: string | string[];
  warnings?: string | string[];
  _errorsList?: string[];
  _warningsList?: string[];
  _summaryList?: string[];
};

type PatientRow = {
  patient_id: string;
  referenceTimestamp: number; // UploadConversationFunction START (or earliest event fallback)
  segments: Segment[];
};

type Props = {
  events: any[]; // can be normalized traces or raw LambdaInsights items
  sortMode?: "latency" | "chronological" | "chronologicalDesc";
  onOpenPatientTrace?: (patientId: string) => void;
  suppressedNames?: string[];
};

const ROW_HEIGHT = 28;
const LABEL_WIDTH = 220;
const MIN_CHART_WIDTH = 900;
const TIMELINE_GRID_STEP_MS = 60000;
// Allow very wide timelines so the emphasis grid line can span half the viewport at max scale
const MAX_CHART_WIDTH = 50000;
const MAX_SCALE = 3;
const ALERT_BAND_HEIGHT = 10; // extra pixels when a row has alerts (smaller with reduced dot size)
const ALERT_DOT_SIZE = 8;
const ALERT_CLUSTER_PX = 10; // cluster dots if within this many pixels
// Normalize any value into an array of non-empty strings
const toStringList = (val: unknown): string[] => {
  const norm = (arr: any[]): string[] =>
    arr
      .map((v) => {
        if (typeof v === "string") return v.trim();
        if (v && typeof v === "object") {
          // Handle Dynamo-style {"S": "..."} objects
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const obj: any = v;
          if (typeof obj.S === "string") return obj.S.trim();
          // Fallback: stringify object
          try {
            return JSON.stringify(obj);
          } catch {
            return String(obj);
          }
        }
        return String(v).trim();
      })
      .filter((s) => s.length > 0);

  try {
    if (Array.isArray(val)) return norm(val);
    if (typeof val === "string") {
      const t = val.trim();
      if (!t) return [];
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return norm(parsed);
      } catch {
        // not JSON array; treat as single string
      }
      return [t];
    }
    return [];
  } catch {
    return [];
  }
};

// Curated high-contrast, colorblind-friendly palette (deterministic mapping)
// Mix of Tableau/ColorBrewer/Okabe-Ito inspired distinct colors
const curatedPalette = [
  "#1f77b4", // blue
  "#ff7f0e", // orange
  "#2ca02c", // green
  "#d62728", // red
  "#9467bd", // purple
  "#8c564b", // brown
  "#e377c2", // pink
  "#7f7f7f", // gray
  "#bcbd22", // olive
  "#17becf", // teal
  "#e41a1c", // red (CB)
  "#377eb8", // blue (CB)
  "#4daf4a", // green (CB)
  "#984ea3", // purple (CB)
  "#ff7f00", // orange (CB)
  "#ffff33", // yellow (CB)
  "#a65628", // brown (CB)
  "#f781bf", // pink (CB)
  "#999999", // gray (CB)
  "#66c2a5"  // aqua
] as const;

const paletteColorFor = (name: string): string => {
  // Simple stable hash to index into palette
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % curatedPalette.length;
  return curatedPalette[idx];
};

function readTimelineEmphasisMarkSeconds(cfg: any): number {
  const s = cfg?.timelineEmphasisMarkSeconds;
  if (typeof s === "number" && Number.isFinite(s) && s >= 0) {
    return s;
  }
  return 180;
}

function formatEmphasisMarkLabel(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const m = Math.floor(seconds / 60);
  const secRem = seconds - m * 60;
  if (m === 0) {
    return Number.isInteger(seconds) ? `${seconds}s` : `${seconds.toFixed(1)}s`;
  }
  if (Math.abs(secRem) < 1e-6) return `${m}m`;
  const sStr = Number.isInteger(secRem) ? `${secRem}` : secRem.toFixed(1);
  return `${m}m${sStr}s`;
}

export default function PatientsTimeline({ events, sortMode = "latency", onOpenPatientTrace, suppressedNames }: Props) {
  const emphasisMarkSeconds = readTimelineEmphasisMarkSeconds(vizConfig);
  const emphasisMarkMs = Math.round(emphasisMarkSeconds * 1000);
  // very lightweight state; no tooltips or zoom for performance
  const [scale, setScale] = useState(1);
  const { toast } = useToast();
  // Track available viewport width for the scrollable timeline area
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(0);
  React.useEffect(() => {
    const update = () => {
      const containerW = containerRef.current?.clientWidth ?? window.innerWidth;
      // Visible timeline area excludes the fixed labels column
      const timelineViewport = Math.max(320, containerW - LABEL_WIDTH);
      setViewportWidth(timelineViewport);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const suppressedSet = useMemo(() => {
    const list =
      (Array.isArray(suppressedNames) ? suppressedNames : (vizConfig?.suppressedLambdas as string[])) || [];
    return new Set(list);
  }, [suppressedNames]);
  // Support multiple session split lambdas via sessionSplitLambdas; fallback to legacy sessionSplitLambda
  const splitLambdaNames: Set<string> = useMemo(() => {
    const arr = Array.isArray((vizConfig as any)?.sessionSplitLambdas)
      ? ((vizConfig as any).sessionSplitLambdas as string[])
      : (typeof (vizConfig as any)?.sessionSplitLambda === "string" &&
        (vizConfig as any).sessionSplitLambda.trim().length > 0
          ? [(vizConfig as any).sessionSplitLambda]
          : []);
    return new Set(arr);
  }, []);
  const explicitLambdaColors: Record<string, string> =
    (vizConfig as any)?.lambdaColors || {};
  const getLambdaColor = (name: string): string =>
    explicitLambdaColors[name] || paletteColorFor(name);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    visible: boolean;
    content: React.ReactNode;
  }>({ x: 0, y: 0, visible: false, content: null });
  // Persistent details dialog (for clicking boxes/dots)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState<string>("");
  const [detailsContent, setDetailsContent] = useState<React.ReactNode>(null);
  const openDetails = (title: string, content: React.ReactNode) => {
    setDetailsTitle(title);
    setDetailsContent(content);
    setDetailsOpen(true);
    setTooltip((t) => ({ ...t, visible: false }));
  };
  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsTitle("");
    setDetailsContent(null);
  };

  // Dialog state for full insights visualization
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [insightsLogs, setInsightsLogs] = useState<any[]>([]);

  // Aggregate diagnostics for the selected patient from today's events
  const patientDiagnostics = useMemo(() => {
    if (!selectedPatientId)
      return { errors: [] as string[], warnings: [] as string[], summary: [] as string[] };
    const errs: string[] = [];
    const warns: string[] = [];
    const sums: string[] = [];
    for (const ev of (events as any[]) || []) {
      if (String(ev?.patient_id ?? "") !== selectedPatientId) continue;
      const eList = toStringList((ev as any).errors);
      const wList = toStringList((ev as any).warnings);
      const sList = toStringList((ev as any).summary);
      errs.push(...eList);
      warns.push(...wList);
      sums.push(...sList);
    }
    return { errors: errs, warnings: warns, summary: sums };
  }, [events, selectedPatientId]);

  const openTraceDialog = async (patientId: string) => {
    setSelectedPatientId(patientId);
    setDialogOpen(true);
    setInsightsLoading(true);
    setInsightsError(null);
    setInsightsLogs([]);
    try {
      const result = await fetchInsightsByPatient(patientId);
      if (result.error) {
        throw new Error(result.error);
      }
      setInsightsLogs(result.data);
    } catch (e: any) {
      setInsightsError(e?.message || "Failed to load insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  const closeTraceDialog = () => {
    setDialogOpen(false);
    setSelectedPatientId(null);
    setInsightsLogs([]);
    setInsightsError(null);
  };

  // Compute global max relative end across all patients to set scale
  const { maxRelativeEnd, rows } = useMemo(() => {
    const toMs = (t: number | string): number => {
      if (typeof t === "number") return t;
      const n = Number(t);
      if (!Number.isNaN(n)) return n;
      const d = new Date(t as string).getTime();
      return Number.isNaN(d) ? 0 : d;
    };

    // Filter suppressed lambdas first (support both traces and insights shapes)
    const filtered = (events || []).filter((ev: any) => {
      if (!ev) return false;
      const name: string | undefined =
        (ev as any).lambda_name ?? (ev as any).artifact;
      if (!name) return false;
      if (splitLambdaNames.size > 0 && splitLambdaNames.has(name)) return true; // never suppress anchors
      return !suppressedSet.has(name);
    });

    // Group events by patient
    const byPatient = new Map<string, any[]>();
    for (const ev of filtered as any[]) {
      const pid = String((ev as any).patient_id ?? "");
      if (!pid) continue;
      if (!byPatient.has(pid)) byPatient.set(pid, []);
      byPatient.get(pid)!.push(ev);
    }

    // Time-gap split configuration
    const timeGapCfg = (vizConfig && vizConfig.timeGapSplit) || {};
    const timeGapEnabled = timeGapCfg.enabled === true;
    const gapMs =
      typeof timeGapCfg.gapMs === "number" && timeGapCfg.gapMs >= 0
        ? Math.floor(timeGapCfg.gapMs)
        : 60000;
    const considerSet = new Set<string>([
      ...Array.from(splitLambdaNames),
      ...((Array.isArray(timeGapCfg.considerLambdas) ? timeGapCfg.considerLambdas : []) as string[]),
    ]);
    const latencySortExcludedSet = new Set<string>(
      Array.isArray((vizConfig as any)?.latencySortExcludedLambdas)
        ? ((vizConfig as any).latencySortExcludedLambdas as string[])
        : []
    );

    type RowType = PatientRow & {
      displayName: string;
      relSegments: Array<Segment & { relStart: number; relEnd: number }>;
      sessionDuration: number;
      latencySortDuration: number;
      splitKind: "anchor" | "timeGap";
      anchorLambdaName?: string;
    };
    const patientGroups: Array<{
      patient_id: string;
      firstStart: number;
      rows: RowType[];
      longestDuration: number;
    }> = [];
    let globalMax = 0;

    for (const [patientId, evs] of byPatient.entries()) {
      // Sort events by start asc (use timestamp as start)
      const sorted = [...evs].sort((a: any, b: any) => {
        const aStart = toMs(a.timestamp ?? a.start_time_ms);
        const bStart = toMs(b.timestamp ?? b.start_time_ms);
        return aStart - bStart;
      });
      // Anchors based on sessionSplitLambdas (or legacy single)
      const anchors = sorted
        .filter((e: any) => {
          const name = e.lambda_name ?? e.artifact;
          const hasStart = e.timestamp != null || e.status === "START";
          return splitLambdaNames.size > 0 ? (splitLambdaNames.has(name) && hasStart) : hasStart;
        })
        .map((e: any) => toMs(e.timestamp ?? e.start_time_ms))
        .sort((a, b) => a - b);

      const makeRowForSlice = (
        sliceEvents: any[],
        referenceTs: number,
        displayName: string,
        splitKind: "anchor" | "timeGap",
        anchorLambdaName?: string
      ): RowType | null => {
        const segments: Segment[] = [];
        const isInsights = sliceEvents.some((x: any) => x.timestamp != null || x.start_time_ms != null);
        if (isInsights) {
          for (const e of sliceEvents) {
            const name = e.artifact || e.lambda_name || "unknown";
            const s = toMs(e.timestamp ?? e.start_time_ms);
            if (!Number.isFinite(s)) continue;
            const dur = toMs(e.time_taken_ms ?? 0);
            const end = s + Math.max(0, dur);
            const uuid = String(e.uuid ?? `${patientId}::${name}::${s}`);
            const errList = toStringList((e as any).errors);
            const warnList = toStringList((e as any).warnings);
            const sumList = toStringList((e as any).summary);
            segments.push({
              lambda_name: name,
              uuid,
              start: s,
              end,
              duration: Math.max(0, end - s),
              patient_id: patientId,
              summary: (e as any).summary,
              errors: (e as any).errors,
              warnings: (e as any).warnings,
              _errorsList: errList,
              _warningsList: warnList,
              _summaryList: sumList,
            });
          }
        } else {
          const byKey = new Map<string, any[]>();
          for (const e of sliceEvents) {
            const key = `${e.lambda_name || "unknown"}::${e.uuid || "unknown"}`;
            if (!byKey.has(key)) byKey.set(key, []);
            byKey.get(key)!.push(e);
          }
          for (const [, arr] of byKey.entries()) {
            const startEv = arr.find((x) => x.status === "START");
            const endEv = arr.find((x) => x.status === "END");
            if (!startEv && endEv) continue;
            if (startEv) {
              const s = toMs(startEv.timestamp);
              const e = endEv ? toMs(endEv.timestamp) : s;
              segments.push({
                lambda_name: startEv.lambda_name || "unknown",
                uuid: startEv.uuid || "unknown",
                start: s,
                end: e,
                duration: Math.max(0, e - s),
                patient_id: patientId,
              });
            }
          }
        }
        segments.sort((a, b) => a.start - b.start);
        if (segments.length === 0) return null;
        const relSegments = segments.map((s) => {
          const relStart = Math.max(0, s.start - referenceTs);
          const relEnd = Math.max(relStart, s.end - referenceTs);
          globalMax = Math.max(globalMax, relEnd);
          return { ...s, relStart, relEnd };
        });
        const sessionDuration =
          relSegments.length > 0
            ? Math.max(...relSegments.map((s) => s.relEnd)) -
              Math.min(...relSegments.map((s) => s.relStart))
            : 0;
        const relForLatencySort = relSegments.filter(
          (s) => !latencySortExcludedSet.has(s.lambda_name)
        );
        const latencySortDuration =
          relForLatencySort.length > 0
            ? Math.max(...relForLatencySort.map((s) => s.relEnd)) -
              Math.min(...relForLatencySort.map((s) => s.relStart))
            : 0;
        return {
          patient_id: patientId,
          displayName,
          referenceTimestamp: referenceTs,
          segments,
          relSegments,
          sessionDuration,
          latencySortDuration,
          splitKind,
          anchorLambdaName,
        };
      };

      let groupRows: RowType[] = [];
      if (splitLambdaNames.size > 0) {
        // When a session split lambda is configured, prefer splitting on anchors.
        // If no anchor present for this patient, SKIP rendering this patient entirely.
        if (anchors.length === 0) {
          // Do nothing; patient omitted when no sessionSplitLambda start exists.
        } else if (anchors.length === 1) {
          // Enforce sessionSplitLambda to be the first event: drop anything before it
          const startTs = anchors[0];
          const slice = sorted.filter((e) => toMs(e.timestamp) >= startTs);
          if (timeGapEnabled) {
            const considered = slice
              .filter((e) => considerSet.has((e.artifact ?? e.lambda_name) as string))
              .map((e) => toMs(e.timestamp ?? e.start_time_ms))
              .sort((a, b) => a - b);
            const boundaries: number[] = [startTs];
            for (let i = 0; i < considered.length - 1; i++) {
              if (considered[i + 1] - considered[i] > gapMs) boundaries.push(considered[i + 1]);
            }
            const subBoundaries = [...boundaries, Number.POSITIVE_INFINITY];
            const subCount = subBoundaries.length - 1;
            for (let i = 0; i < subCount; i++) {
              const subStart = subBoundaries[i];
              const subEnd = subBoundaries[i + 1];
              const subSlice = slice.filter((e) => {
                const ts = toMs(e.timestamp ?? e.start_time_ms);
                return ts >= subStart && ts < subEnd;
              });
              if (subSlice.length === 0) continue;
              let anchorLambdaName: string | undefined = undefined;
              if (i === 0) {
                const anchorEvent = subSlice.find(
                  (e) =>
                    toMs(e.timestamp ?? e.start_time_ms) === startTs &&
                    splitLambdaNames.has((e.artifact ?? e.lambda_name) as string)
                );
                anchorLambdaName =
                  (anchorEvent && ((anchorEvent.artifact ?? anchorEvent.lambda_name) as string)) || undefined;
              }
              const kind: "anchor" | "timeGap" = i === 0 ? "anchor" : "timeGap";
              // If there is only one sub-slice (no time-gap), do not show numbering
              const display =
                subCount === 1 ? `${patientId}` : `${patientId} (1.${i + 1})`;
              const row = makeRowForSlice(subSlice, subStart, display, kind, anchorLambdaName);
              if (row) groupRows.push(row);
            }
          } else {
            const row = makeRowForSlice(slice, startTs, patientId, "anchor");
            if (row) groupRows.push(row);
          }
        } else {
          // Multiple sessions; split events per session start boundary
          const boundaries = [...anchors, Number.POSITIVE_INFINITY];
          for (let i = 0; i < anchors.length; i++) {
            const startTs = anchors[i];
            const endBoundary = boundaries[i + 1];
            const slice = sorted.filter((e) => {
              const ts = toMs(e.timestamp);
              return ts >= startTs && ts < endBoundary;
            });
            if (timeGapEnabled) {
              const considered = slice
                .filter((e) => considerSet.has((e.artifact ?? e.lambda_name) as string))
                .map((e) => toMs(e.timestamp ?? e.start_time_ms))
                .sort((a, b) => a - b);
              const gapStarts: number[] = [startTs];
              for (let j = 0; j < considered.length - 1; j++) {
                if (considered[j + 1] - considered[j] > gapMs) gapStarts.push(considered[j + 1]);
              }
              const subBoundaries = [...gapStarts, Number.POSITIVE_INFINITY];
              const subCount = subBoundaries.length - 1;
              for (let j = 0; j < subCount; j++) {
                const subStart = subBoundaries[j];
                const subEnd = subBoundaries[j + 1];
                const subSlice = slice.filter((e) => {
                  const ts = toMs(e.timestamp ?? e.start_time_ms);
                  return ts >= subStart && ts < subEnd;
                });
                if (subSlice.length === 0) continue;
                let anchorLambdaName: string | undefined = undefined;
                if (j === 0) {
                  const anchorEvent = subSlice.find(
                    (e) =>
                      toMs(e.timestamp ?? e.start_time_ms) === startTs &&
                      splitLambdaNames.has((e.artifact ?? e.lambda_name) as string)
                  );
                  anchorLambdaName =
                    (anchorEvent && ((anchorEvent.artifact ?? anchorEvent.lambda_name) as string)) || undefined;
                }
                const kind: "anchor" | "timeGap" = j === 0 ? "anchor" : "timeGap";
                // If no time gaps within this anchor (single sub-slice), show only integer session index
                const display =
                  subCount === 1
                    ? `${patientId} (${i + 1})`
                    : `${patientId} (${i + 1}.${j + 1})`;
                const row = makeRowForSlice(subSlice, subStart, display, kind, anchorLambdaName);
                if (row) groupRows.push(row);
              }
            } else {
              const row = makeRowForSlice(
                slice,
                startTs,
                `${patientId} (${i + 1})`,
                "anchor"
              );
              if (row) groupRows.push(row);
            }
          }
        }
      } else {
        // No session split lambda configured; fallback to earliest event
        const earliestStart = toMs((sorted[0] as any)?.timestamp ?? (sorted[0] as any)?.start_time_ms ?? 0);
        if (Number.isFinite(earliestStart)) {
          if (timeGapEnabled) {
            const considered = sorted
              .filter((e) => considerSet.has((e.artifact ?? e.lambda_name) as string))
              .map((e) => toMs(e.timestamp ?? e.start_time_ms))
              .sort((a, b) => a - b);
            const boundaries: number[] = [earliestStart];
            for (let i = 0; i < considered.length - 1; i++) {
              if (considered[i + 1] - considered[i] > gapMs) boundaries.push(considered[i + 1]);
            }
            const subBoundaries = [...boundaries, Number.POSITIVE_INFINITY];
            const subCount = subBoundaries.length - 1;
            for (let i = 0; i < subCount; i++) {
              const subStart = subBoundaries[i];
              const subEnd = subBoundaries[i + 1];
              const subSlice = sorted.filter((e) => {
                const ts = toMs(e.timestamp ?? e.start_time_ms);
                return ts >= subStart && ts < subEnd;
              });
              if (subSlice.length === 0) continue;
              const display =
                subCount === 1 ? `${patientId}` : `${patientId} (1.${i + 1})`;
              const row = makeRowForSlice(subSlice, subStart, display, "timeGap");
              if (row) groupRows.push(row);
            }
          } else {
            const row = makeRowForSlice(sorted, earliestStart, patientId, "anchor");
            if (row) groupRows.push(row);
          }
        }
      }

      if (groupRows.length > 0) {
        groupRows.sort((a, b) => a.referenceTimestamp - b.referenceTimestamp);
        const longestDuration = Math.max(
          ...groupRows.map((r) => r.latencySortDuration || 0)
        );
        patientGroups.push({
          patient_id: patientId,
          firstStart: groupRows[0].referenceTimestamp,
          rows: groupRows,
          longestDuration,
        });
      }
    }

    // Sort patients by selected mode. Within patient keep session order.
    if (sortMode === "chronological") {
      patientGroups.sort((a, b) => a.firstStart - b.firstStart);
    } else if (sortMode === "chronologicalDesc") {
      patientGroups.sort((a, b) => b.firstStart - a.firstStart);
    } else {
      patientGroups.sort((a, b) => b.longestDuration - a.longestDuration);
    }
    const rows: RowType[] = [];
    for (const g of patientGroups) rows.push(...g.rows);

    return { maxRelativeEnd: globalMax, rows };
  }, [events, suppressedSet, sortMode]);

  const chartWidth = useMemo(() => {
    if (maxRelativeEnd <= 0) return MIN_CHART_WIDTH;
    // Start with baseline width proportional to total duration (3px/sec)
    let base = Math.max(MIN_CHART_WIDTH, (maxRelativeEnd / 1000) * 3);
    // Enforce: at max scale, the emphasis mark (viz-config timelineEmphasisMarkSeconds) should be at least half of the visible viewport.
    // x(mark) = (emphasisMarkMs / maxRelativeEnd) * (base * scale) >= 0.5 * viewportWidth
    if (
      scale >= MAX_SCALE - 1e-6 &&
      maxRelativeEnd >= emphasisMarkMs &&
      emphasisMarkMs > 0 &&
      viewportWidth > 0
    ) {
      const neededFinalWidth = 0.5 * viewportWidth * (maxRelativeEnd / emphasisMarkMs);
      const neededBase = neededFinalWidth / scale;
      base = Math.max(base, neededBase);
    }
    // Hard cap to avoid runaway widths
    base = Math.min(base, MAX_CHART_WIDTH);
    return base * scale;
  }, [maxRelativeEnd, scale, viewportWidth, emphasisMarkMs]);

  // Compute total dynamic height: add alert band only for rows that have alerts
  const totalHeight = useMemo(() => {
    let h = 0;
    for (const row of rows) {
      const hasAlerts = row.relSegments.some(
        (s: any) =>
          (typeof s.errors === "string" && s.errors.trim().length > 0) ||
          (typeof s.warnings === "string" && s.warnings.trim().length > 0)
      );
      h += ROW_HEIGHT + (hasAlerts ? ALERT_BAND_HEIGHT : 0);
    }
    return h + 8 + 8; // paddings
  }, [rows]);

  return (
    <div className="w-full">
      {/* Minimal controls */}
      <div className="mb-3 flex items-center gap-4 text-xs text-neutral-600">
        <div className="flex items-center gap-2">
          <span>Scale</span>
          <input type="range" min={0.5} max={MAX_SCALE} step={0.1} value={scale} onChange={(e) => setScale(Number(e.target.value))} />
        </div>
        <div className="text-neutral-500">Showing {rows.length} patients</div>
      </div>
      <div
        className="relative border rounded-lg overflow-hidden"
        style={{
          height: totalHeight,
        }}
        ref={containerRef}
      >
        {/* Labels */}
        <div
          className="absolute top-0 left-0 bg-white z-10 border-r overflow-hidden"
          style={{
            width: LABEL_WIDTH,
            height: "100%",
            paddingTop: 16,
            paddingBottom: 4,
          }}
        >
          {rows.map((row) => {
            const hasAlerts = row.relSegments.some(
              (s: any) =>
                toStringList((s as any).errors || (s as any)._errorsList).length >
                  0 ||
                toStringList((s as any).warnings || (s as any)._warningsList)
                  .length > 0
            );
            const rowHeight = ROW_HEIGHT + (hasAlerts ? ALERT_BAND_HEIGHT : 0);
            return (
              <div
                key={`${row.patient_id}-${row.referenceTimestamp}`}
                className="px-3 flex items-center border-b text-xs text-neutral-700"
                style={{ height: rowHeight }}
                title={row.displayName || row.patient_id}
              >
                <div className="flex items-start gap-2">
                  <div className="leading-tight">
                    {(() => {
                      const colors = (vizConfig && vizConfig.splitLabelColors) || {};
                      const timeGapColor = typeof colors.timeGap === "string" ? colors.timeGap : "#f59e0b";
                      const uploadColor =
                        typeof colors.UploadConversationFunction === "string" ? colors.UploadConversationFunction : "#000000";
                      const mobileColor =
                        typeof colors.MobileAudioProcessorFunction === "string"
                          ? colors.MobileAudioProcessorFunction
                          : "#2563eb";
                      let labelColor = "#2c2c2c";
                      if (row.splitKind === "timeGap") {
                        labelColor = timeGapColor;
                      } else if (row.anchorLambdaName === "UploadConversationFunction") {
                        labelColor = uploadColor;
                      } else if (row.anchorLambdaName === "MobileAudioProcessorFunction") {
                        labelColor = mobileColor;
                      }
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            onOpenPatientTrace
                              ? onOpenPatientTrace(row.patient_id)
                              : openTraceDialog(row.patient_id)
                          }
                          className="truncate hover:underline text-left"
                          title={`Open trace for ${row.displayName || row.patient_id}`}
                          style={{ color: labelColor }}
                        >
                          {row.displayName || row.patient_id}
                        </button>
                      );
                    })()}
                    <div className="text-[10px] text-neutral-500">
                      {new Date(row.referenceTimestamp).toLocaleTimeString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-neutral-500 hover:text-neutral-800"
                    title="Copy patient id"
                    aria-label="Copy patient id"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(row.patient_id);
                      } catch {
                        // fallback
                        const ta = document.createElement("textarea");
                        ta.value = row.patient_id;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                      toast({
                        title: "Copied",
                        description: `Patient ID ${row.patient_id} copied`,
                      });
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Timeline */}
        <div
          className="absolute overflow-x-auto"
          style={{
            left: LABEL_WIDTH,
            right: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <div
            className="relative"
            style={{
              width: chartWidth,
              height: totalHeight,
              paddingTop: 16,
              paddingBottom: 4,
            }}
          >
            {/* Minute grid lines across all rows */}
            <div className="absolute inset-0 pointer-events-none">
              {Array.from(
                {
                  length:
                    Math.max(1, Math.ceil(maxRelativeEnd / TIMELINE_GRID_STEP_MS)) + 1,
                },
                (_, i) => {
                  const rel = i * TIMELINE_GRID_STEP_MS;
                  const x =
                    maxRelativeEnd > 0 ? (rel / maxRelativeEnd) * chartWidth : 0;
                  return (
                    <div
                      key={`grid-${i}`}
                      className="absolute top-0 bottom-0 border-l border-neutral-200"
                      style={{ left: x }}
                    >
                      <span className="absolute -translate-x-1/2 -top-3 text-[10px] text-neutral-400 bg-white px-0.5">
                        {i}m
                      </span>
                    </div>
                  );
                }
              )}
              {emphasisMarkMs > 0 &&
                maxRelativeEnd > 0 &&
                emphasisMarkMs <= maxRelativeEnd && (
                  <div
                    className="absolute top-0 bottom-0 border-l border-red-500 z-[1]"
                    style={{
                      left: (emphasisMarkMs / maxRelativeEnd) * chartWidth,
                    }}
                  >
                    <span className="absolute -translate-x-1/2 -top-3 text-[10px] text-red-600 font-semibold bg-white px-0.5 whitespace-nowrap">
                      {formatEmphasisMarkLabel(emphasisMarkSeconds)}
                    </span>
                  </div>
                )}
            </div>

            {rows.map((row, rowIdx) => {
              const hasAlerts = row.relSegments.some((s: any) => {
                const hasErr =
                  toStringList((s as any).errors || (s as any)._errorsList)
                    .length > 0;
                const hasWarn =
                  toStringList(
                    (s as any).warnings || (s as any)._warningsList
                  ).length > 0;
                return hasErr || hasWarn;
              });
              const rowHeight = ROW_HEIGHT + (hasAlerts ? ALERT_BAND_HEIGHT : 0);
              return (
                <div
                  key={`${row.patient_id}-${row.referenceTimestamp}`}
                  className="relative"
                  style={{ height: rowHeight }}
                >
                {row.relSegments.map((seg) => {
                  const left =
                    maxRelativeEnd > 0
                      ? (seg.relStart / maxRelativeEnd) * chartWidth
                      : 0;
                  const width =
                    maxRelativeEnd > 0
                      ? Math.max(
                          2,
                          ((seg.relEnd - seg.relStart) / maxRelativeEnd) *
                            chartWidth
                        )
                      : 2;
                  const color = getLambdaColor(seg.lambda_name);
                  return (
                    <div
                      key={`${seg.uuid}-${seg.lambda_name}`}
                      className="absolute top-1 h-5 rounded opacity-90 cursor-pointer"
                      style={{
                        left,
                        width,
                        backgroundColor: color,
                      }}
                      onMouseMove={(e) => {
                        // Show original stats table on hover
                        const startLocal = new Date(seg.start).toLocaleString();
                        const endLocal = new Date(seg.end).toLocaleString();
                        const formattedDuration = `${Number(seg.duration || 0).toLocaleString()} ms`;
                        const table = (
                          <table className="text-[11px]">
                            <tbody>
                              <tr>
                                <td className="pr-2 font-medium text-neutral-600">Lambda</td>
                                <td className="text-neutral-800">{seg.lambda_name}</td>
                              </tr>
                              <tr>
                                <td className="pr-2 font-medium text-neutral-600">Patient</td>
                                <td className="text-neutral-800">{seg.patient_id}</td>
                              </tr>
                              <tr>
                                <td className="pr-2 font-medium text-neutral-600">Start</td>
                                <td className="text-neutral-800">{startLocal}</td>
                              </tr>
                              <tr>
                                <td className="pr-2 font-medium text-neutral-600">End</td>
                                <td className="text-neutral-800">{endLocal}</td>
                              </tr>
                              <tr>
                                <td className="pr-2 font-medium text-neutral-600">Duration</td>
                                <td className="text-neutral-800">{formattedDuration}</td>
                              </tr>
                            </tbody>
                          </table>
                        );
                        setTooltip({
                          x: e.clientX + 12,
                          y: e.clientY + 12,
                          visible: true,
                          content: table,
                        });
                      }}
                      onMouseLeave={() =>
                        setTooltip((t) => ({ ...t, visible: false }))
                      }
                    />
                  );
                })}
                {/* Alerts indicator band (dots) */}
                {(() => {
                  const alerts = row.relSegments.filter((s) => {
                    const hasErr = toStringList((s as any).errors || (s as any)._errorsList).length > 0;
                    const hasWarn = toStringList((s as any).warnings || (s as any)._warningsList).length > 0;
                    return hasErr || hasWarn;
                  });
                  if (alerts.length === 0) return null;
                  // Compute dot positions and cluster close ones
                  const pts = alerts
                    .map((s) => {
                      const mid = (s.relStart + s.relEnd) / 2;
                      const x =
                        maxRelativeEnd > 0
                          ? (mid / maxRelativeEnd) * chartWidth
                          : 0;
                      const hasErr = toStringList((s as any).errors || (s as any)._errorsList).length > 0;
                      const hasWarn = !hasErr && toStringList((s as any).warnings || (s as any)._warningsList).length > 0;
                      return { x, seg: s, hasErr, hasWarn };
                    })
                    .sort((a, b) => a.x - b.x);
                  type Cluster = {
                    xCenter: number;
                    items: typeof pts;
                    hasErr: boolean;
                    hasWarn: boolean;
                  };
                  const clusters: Array<{
                    xCenter: number;
                    items: Array<typeof pts[number]>;
                    hasErr: boolean;
                    hasWarn: boolean;
                  }> = [];
                  for (const p of pts) {
                    const last = clusters[clusters.length - 1];
                    if (!last || Math.abs(p.x - last.xCenter) > ALERT_CLUSTER_PX) {
                      clusters.push({
                        xCenter: p.x,
                        items: [p],
                        hasErr: p.hasErr,
                        hasWarn: !p.hasErr && p.hasWarn,
                      });
                    } else {
                      last.items.push(p);
                      // Recompute center (simple average)
                      last.xCenter =
                        (last.xCenter * (last.items.length - 1) + p.x) /
                        last.items.length;
                      last.hasErr = last.hasErr || p.hasErr;
                      last.hasWarn = last.hasWarn || p.hasWarn;
                    }
                  }
                  return (
                    <div className="absolute left-0 right-0" style={{ bottom: 2, height: ALERT_BAND_HEIGHT }}>
                      {clusters.map((c, i) => {
                        const bg = c.hasErr
                          ? "#b91c1c" // red-700 for stronger prominence
                          : c.hasWarn
                          ? "#d97706" // amber-600 for stronger prominence
                          : "#6b7280"; // neutral-500 as fallback
                        const left =
                          Math.max(0, Math.min(chartWidth - ALERT_DOT_SIZE, c.xCenter - ALERT_DOT_SIZE / 2));
                        const obj =
                          c.items.length === 1
                            ? (() => {
                                const s = c.items[0].seg as any;
                                const detail: any = {};
                                const errs = toStringList(s.errors || s._errorsList);
                                const warns = toStringList(s.warnings || s._warningsList);
                                if (errs.length > 0) detail.errors = errs;
                                if (warns.length > 0) detail.warnings = warns;
                                return detail;
                              })()
                            : {
                                count: c.items.length,
                                notes: c.items.map(({ seg }) => {
                                  const s = seg as any;
                                  const note: any = {};
                                  const errs = toStringList(s.errors || s._errorsList);
                                  const warns = toStringList(s.warnings || s._warningsList);
                                  if (errs.length > 0) note.errors = errs;
                                  if (warns.length > 0) note.warnings = warns;
                                  return note;
                                }),
                              };
                        const json = JSON.stringify(obj, null, 2);
                        return (
                          <div
                            key={`cluster-${rowIdx}-${i}`}
                            className="absolute rounded-full ring-2 ring-white shadow-lg flex items-center justify-center transition-transform transform hover:scale-150 cursor-pointer"
                            style={{
                              left,
                              width: ALERT_DOT_SIZE,
                              height: ALERT_DOT_SIZE,
                              backgroundColor: bg,
                            }}
                            onMouseMove={(e) => {
                              // Build a table of warnings/errors for this cluster
                              const rows: Array<{ type: "error" | "warning"; text: string }> =
                                c.items.length === 1
                                  ? (() => {
                                      const s = c.items[0].seg as any;
                                      const errs = toStringList(s.errors || s._errorsList);
                                      const warns = toStringList(s.warnings || s._warningsList);
                                      return [
                                        ...errs.map((t) => ({ type: "error" as const, text: t })),
                                        ...warns.map((t) => ({ type: "warning" as const, text: t })),
                                      ];
                                    })()
                                  : c.items.flatMap(({ seg }) => {
                                      const s = seg as any;
                                      const errs = toStringList(s.errors || s._errorsList);
                                      const warns = toStringList(s.warnings || s._warningsList);
                                      return [
                                        ...errs.map((t) => ({ type: "error" as const, text: t })),
                                        ...warns.map((t) => ({ type: "warning" as const, text: t })),
                                      ];
                                    });
                              const table = (
                                <table className="text-[11px] min-w-[240px]">
                                  <tbody>
                                    {rows.map((r, idx2) => (
                                      <tr key={`r-${idx2}`}>
                                        <td
                                          className="px-2 py-1 font-semibold text-white"
                                          style={{
                                            whiteSpace: "nowrap",
                                            backgroundColor:
                                              r.type === "error" ? "#b91c1c" : "#d97706" // red-700 / amber-600
                                          }}
                                        >
                                          {r.type === "error" ? "Error" : "Warning"}
                                        </td>
                                        <td
                                          className="px-2 py-1 text-neutral-900"
                                          style={{
                                            backgroundColor:
                                              r.type === "error" ? "#fee2e2" : "#fef3c7", // red-100 / amber-100
                                            borderLeft:
                                              r.type === "error" ? "3px solid #b91c1c" : "3px solid #d97706"
                                          }}
                                        >
                                          {r.text}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                              setTooltip({
                                x: e.clientX + 12,
                                y: e.clientY + 12,
                                visible: true,
                                content: table,
                              });
                            }}
                            onMouseLeave={() =>
                              setTooltip((t) => ({ ...t, visible: false }))
                            }
                            onClick={() => {
                              // Persistent dialog content for the cluster
                              const rows: Array<{ type: "error" | "warning"; text: string }> =
                                c.items.length === 1
                                  ? (() => {
                                      const s = c.items[0].seg as any;
                                      const errs = toStringList(s.errors || s._errorsList);
                                      const warns = toStringList(s.warnings || s._warningsList);
                                      return [
                                        ...errs.map((t) => ({ type: "error" as const, text: t })),
                                        ...warns.map((t) => ({ type: "warning" as const, text: t })),
                                      ];
                                    })()
                                  : c.items.flatMap(({ seg }) => {
                                      const s = seg as any;
                                      const errs = toStringList(s.errors || s._errorsList);
                                      const warns = toStringList(s.warnings || s._warningsList);
                                      return [
                                        ...errs.map((t) => ({ type: "error" as const, text: t })),
                                        ...warns.map((t) => ({ type: "warning" as const, text: t })),
                                      ];
                                    });
                              const table = (
                                <div className="p-1">
                                  <table className="text-[12px] min-w-[420px]">
                                    <tbody>
                                      {rows.map((r, idx2) => (
                                        <tr key={`pd-${idx2}`}>
                                          <td
                                            className="px-2 py-1 font-semibold text-white"
                                            style={{
                                              whiteSpace: "nowrap",
                                              backgroundColor:
                                                r.type === "error" ? "#b91c1c" : "#d97706"
                                            }}
                                          >
                                            {r.type === "error" ? "Error" : "Warning"}
                                          </td>
                                          <td
                                            className="px-2 py-1 text-neutral-900"
                                            style={{
                                              backgroundColor:
                                                r.type === "error" ? "#fee2e2" : "#fef3c7",
                                              borderLeft:
                                                r.type === "error"
                                                  ? "3px solid #b91c1c"
                                                  : "3px solid #d97706"
                                            }}
                                          >
                                            {r.text}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              );
                              openDetails(
                                c.hasErr
                                  ? `Alerts (${rows.length}) – Errors and warnings`
                                  : `Alerts (${rows.length}) – Warnings`,
                                table
                              );
                            }}
                          >
                            {c.items.length > 1 && (
                              <span
                                className="text-[9px] leading-none font-semibold"
                                style={{ color: "#ffffff" }}
                                title={`${c.items.length} alerts`}
                              >
                                {c.items.length}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* Row separator */}
                <div className="absolute left-0 right-0 bottom-0 border-b" />
              </div>
              );
            })}
          </div>
        </div>
        {/* Tooltip content for the hovered block or dot */}
        {tooltip.visible && (
          <div
            ref={tooltipRef}
            className="fixed z-50 bg-white p-3 rounded shadow border text-xs max-w-md max-h-80 overflow-auto"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <div className="space-y-1">{tooltip.content}</div>
          </div>
        )}
      </div>

      {/* Full insights dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-[98vw] max-w-[1600px] max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-sm">
                Insights – {selectedPatientId}
              </div>
              <button
                type="button"
                onClick={closeTraceDialog}
                className="text-sm text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>
            <div className="p-4">
              {insightsLoading && (
                <div className="text-sm text-neutral-600">Loading…</div>
              )}
              {insightsError && (
                <div className="text-sm text-red-600">{insightsError}</div>
              )}
              {!insightsLoading && !insightsError && insightsLogs.length === 0 && (
                <div className="text-sm text-neutral-600">
                  No insight events found.
                </div>
              )}
              {!insightsLoading && !insightsError && insightsLogs.length > 0 && (
                <div className="mt-2">
                  <InsightsTimeline data={insightsLogs} labelMappings={labelMappings} />
                </div>
              )}
              {/* Diagnostics summary for this patient */}
              {(patientDiagnostics.errors.length > 0 ||
                patientDiagnostics.warnings.length > 0 ||
                patientDiagnostics.summary.length > 0) && (
                <div className="mt-4">
                  <div className="text-sm font-semibold mb-2">Diagnostics</div>
                  {patientDiagnostics.errors.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-red-700 mb-1">Errors</div>
                      <div className="flex flex-wrap gap-2">
                        {patientDiagnostics.errors.map((msg, idx) => (
                          <div
                            key={`err-${idx}`}
                            className="px-2 py-1 rounded border text-xs"
                            style={{
                              backgroundColor: "#fecaca", // red-200
                              color: "#7f1d1d", // red-900
                              borderColor: "#dc2626", // red-600
                            }}
                            title={msg}
                          >
                            {msg}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {patientDiagnostics.warnings.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-amber-700 mb-1">Warnings</div>
                      <div className="flex flex-wrap gap-2">
                        {patientDiagnostics.warnings.map((msg, idx) => (
                          <div
                            key={`warn-${idx}`}
                            className="px-2 py-1 rounded border text-xs"
                            style={{
                              backgroundColor: "#fde68a", // amber-300
                              color: "#7c2d12", // amber-900
                              borderColor: "#d97706", // amber-600
                            }}
                            title={msg}
                          >
                            {msg}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {patientDiagnostics.warnings.length > 0 && patientDiagnostics.summary.length > 0 && (
                    <div className="h-2" />
                  )}
                  {patientDiagnostics.summary.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-neutral-700 mb-1">Summary</div>
                      <div className="flex flex-wrap gap-2">
                        {patientDiagnostics.summary.map((msg, idx) => (
                          <div
                            key={`sum-${idx}`}
                            className="px-2 py-1 rounded border text-xs"
                            style={{
                              backgroundColor: "#eef2ff", // indigo-50
                              color: "#3730a3", // indigo-800
                              borderColor: "#c7d2fe", // indigo-300
                            }}
                            title={msg}
                          >
                            {msg}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Persistent details dialog for clicked box/dot */}
      {detailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-[900px] max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-sm">{detailsTitle || "Details"}</div>
              <button
                type="button"
                onClick={closeDetails}
                className="text-sm text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>
            <div className="p-4">{detailsContent}</div>
          </div>
        </div>
      )}
    </div>
  );
}

